import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X, ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { content } from "../lib/i18n";

const LangButton = ({ value, current, setLang, large = false }) => {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={() => setLang(value)}
      data-testid={`lang-toggle-${value}`}
      aria-pressed={active}
      aria-label={value === "fr" ? "Français" : "English"}
      className={`bb-lang-btn ${active ? "active" : ""} ${
        large ? "bb-lang-btn-lg" : ""
      }`}
    >
      <span>{value.toUpperCase()}</span>
    </button>
  );
};

const Header = () => {
  const { lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navItems = lang === "en" ? content.nav.en : content.nav.fr;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close mobile menu on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <motion.header
        initial={{ y: -16, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        data-testid="site-header"
        className={`bb-header fixed top-0 left-0 right-0 z-50 ${
          scrolled ? "scrolled" : ""
        }`}
      >
        <div className="mx-auto max-w-[1320px] px-5 md:px-8 lg:px-12 h-[64px] md:h-[76px] flex items-center justify-between gap-4">
          {/* Logo */}
          <a
            href="#top"
            data-testid="header-logo"
            className="flex items-center select-none shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--bb-yellow)] rounded"
            aria-label="Big Boss Snack Bar"
          >
            <img
              src={`${process.env.PUBLIC_URL || ""}/brand/logo-dark.png`}
              alt="Big Boss Snack Bar"
              className="h-[35px] md:h-12 w-auto object-contain block"
              style={{ maxWidth: "190px" }}
            />
          </a>

          {/* Desktop nav */}
          <nav
            className="hidden lg:flex items-center gap-7 xl:gap-9"
            data-testid="desktop-nav"
            aria-label="Primary"
          >
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-link-${item.label
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                className="bb-nav-link"
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Right side: lang switcher + order + burger */}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <div
              className="hidden sm:flex items-center gap-2"
              data-testid="lang-toggle-group"
            >
              <LangButton value="fr" current={lang} setLang={setLang} />
              <LangButton value="en" current={lang} setLang={setLang} />
            </div>

            <a
              href={content.externalLinks.order}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-order-cta"
              className="hidden md:inline-flex bb-btn bb-btn-yellow bb-btn-sm"
            >
              <span>{lang === "en" ? "Order" : "Commander"}</span>
              <ArrowUpRight size={14} strokeWidth={2.5} />
            </a>

            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label={lang === "en" ? "Open menu" : "Ouvrir le menu"}
              aria-expanded={open}
              data-testid="mobile-menu-open"
              className="lg:hidden p-2 -mr-2 text-black rounded-full hover:bg-black/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--bb-yellow)]"
            >
              <MenuIcon size={26} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile fullscreen menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden fixed inset-0 z-[70] bg-white flex flex-col"
            data-testid="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label={lang === "en" ? "Mobile navigation" : "Navigation mobile"}
          >
            {/* Top bar inside drawer (logo + close) */}
            <div className="h-[64px] md:h-[76px] px-5 md:px-8 flex items-center justify-between border-b border-black/10 shrink-0">
              <img
                src={`${process.env.PUBLIC_URL || ""}/brand/logo-dark.png`}
                alt="Big Boss Snack Bar"
                className="h-[35px] w-auto object-contain"
                style={{ maxWidth: "180px" }}
              />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={lang === "en" ? "Close menu" : "Fermer le menu"}
                data-testid="mobile-menu-close"
                className="p-2 -mr-2 text-black rounded-full hover:bg-black/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--bb-yellow)]"
              >
                <X size={26} strokeWidth={2.5} />
              </button>
            </div>

            {/* Centered nav */}
            <div className="flex-1 flex flex-col justify-center px-6">
              <motion.nav
                initial="hidden"
                animate="show"
                variants={{
                  hidden: {},
                  show: { transition: { staggerChildren: 0.05, delayChildren: 0.05 } },
                }}
                className="flex flex-col items-center gap-5"
                aria-label={lang === "en" ? "Mobile primary" : "Mobile principale"}
              >
                {navItems.map((item, idx) => (
                  <motion.a
                    key={item.href}
                    href={item.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${item.label
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      show: { opacity: 1, y: 0 },
                    }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="font-display text-[20px] sm:text-[22px] uppercase tracking-wide text-black flex items-baseline gap-3 hover:text-black/70 transition-colors"
                  >
                    <span className="font-body text-[12px] tracking-[0.3em] text-[var(--bb-yellow-deep)] font-bold">
                      0{idx + 1}
                    </span>
                    <span>{item.label}</span>
                  </motion.a>
                ))}

                <motion.a
                  href={content.externalLinks.order}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-order-cta"
                  onClick={() => setOpen(false)}
                  variants={{
                    hidden: { opacity: 0, y: 10 },
                    show: { opacity: 1, y: 0 },
                  }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="bb-btn bb-btn-yellow bb-btn-lg mt-6 w-full max-w-xs justify-center"
                >
                  <span>{lang === "en" ? "Order Now" : "Commander"}</span>
                  <ArrowUpRight size={16} strokeWidth={2.5} />
                </motion.a>

                <motion.a
                  href={content.externalLinks.reservation}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-reserve-cta"
                  onClick={() => setOpen(false)}
                  variants={{
                    hidden: { opacity: 0, y: 10 },
                    show: { opacity: 1, y: 0 },
                  }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="bb-btn bb-btn-outline bb-btn-lg w-full max-w-xs justify-center"
                >
                  <span>{lang === "en" ? "Reserve Table" : "Réserver"}</span>
                  <ArrowUpRight size={16} strokeWidth={2.5} />
                </motion.a>
              </motion.nav>
            </div>

            {/* Lang switcher inside mobile menu */}
            <div className="px-6 pb-24 pt-6 border-t border-black/10 shrink-0">
              <p className="text-[11px] tracking-[0.28em] uppercase text-black/55 font-bold mb-3 text-center">
                {lang === "en" ? "Language" : "Langue"}
              </p>
              <div
                className="flex items-center justify-center gap-3"
                data-testid="mobile-lang-toggle-group"
              >
                <LangButton value="fr" current={lang} setLang={setLang} large />
                <LangButton value="en" current={lang} setLang={setLang} large />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
